import type { HttpRequest } from '@angular/common/http';
import { Injectable, type OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, shareReplay, of, type Observable } from 'rxjs';
import { routes } from '@tig-dpqa-cloud/contract-backend-frontend';
import { findTrackedContractRoute, getTrackedContractRoutes } from '../request-activity-route-matcher';

type Request = {
  method: string;
  url: string;
  startedAt: number;
  contractPath?: string;
  contractKey?: string;
};

const trackedRoutes = getTrackedContractRoutes(routes);

const toBusyState$ = (subject: BehaviorSubject<{ [key: string]: Request }>) =>
  subject.pipe(
    map((requests) => Object.keys(requests).length > 0),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

@Injectable({ providedIn: 'root' })
export class LoadingQueueService implements OnDestroy {
  private readonly router = inject(Router, { optional: true });

  private readonly getRequestsSubject = new BehaviorSubject<{ [key: string]: Request }>({});
  private readonly actRequestsSubject = new BehaviorSubject<{ [key: string]: Request }>({});

  readonly fetching$ = toBusyState$(this.getRequestsSubject);

  readonly acting$ = toBusyState$(this.actRequestsSubject);
  private readonly initialLoadingSubject = new BehaviorSubject<boolean>(true);
  readonly initialLoading$ = this.initialLoadingSubject.asObservable();

  readonly busy$ = combineLatest([this.fetching$, this.acting$, this.initialLoading$]).pipe(
    map(([fetching, acting, initialLoading]) => fetching || acting || initialLoading),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    if (!this.router) {
      // In contexts without a Router (e.g. some unit tests), do not keep initialLoading$ stuck at true.
      this.initialLoadingSubject.next(false);
    }
    // Clear queues on navigation start (navigating away)
    this.router?.events.pipe(
      filter((event) => event instanceof NavigationStart),
      filter(() => this.router?.currentNavigation?.()?.extras?.queryParamsHandling !== 'merge'),
      takeUntilDestroyed(),
    ).subscribe(() => {
      this.clear();
    });

    // Fired after Angular has resolved the route, loaded any lazy chunks, and rendered the component.
    this.router?.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(() => {
      this.initialLoadingSubject.next(false);
    });
  }

  ngOnDestroy(): void {
    this.clear();
  }

  start(requestId: string, req: HttpRequest<unknown>): void {
    const subject = this.getSubjectForMethod(req.method);
    if (!subject) {
      return;
    }
    const contractMatch = this.findContractRouteMatch(req.url, req.method);
    const newValue = {
      ...subject.value,
      [requestId]: {
        method: req.method,
        url: req.urlWithParams,
        startedAt: Date.now(),
        contractPath: contractMatch?.path,
        contractKey: contractMatch?.key,
      },
    };
    subject.next(newValue);
  }

  end(requestId: string): void {
    const getCurrent = this.getRequestsSubject.value;
    if (requestId in getCurrent) {
      const { [requestId]: _get, ...remainingGet } = getCurrent;
      this.getRequestsSubject.next(remainingGet);
    }
    const actCurrent = this.actRequestsSubject.value;
    if (requestId in actCurrent) {
      const { [requestId]: _act, ...remainingAct } = actCurrent;
      this.actRequestsSubject.next(remainingAct);
    }
  }

  clear(): void {
    this.getRequestsSubject.next({});
    this.actRequestsSubject.next({});
  }

  private findContractRouteMatch(url: string, method: string): { key: string; path: string } | undefined {
    return findTrackedContractRoute(url, method, trackedRoutes);
  }

  /**
   * Returns an observable that emits whether any tracked request matching the
   * given URL and method is currently active in the queue.
   */
  isBusyForUrl$(url: string, method: string): Observable<boolean> {
    const match = this.findContractRouteMatch(url, method);
    if (!match) {
      return of(false);
    }

    const contractKey = match.key;
    return combineLatest([this.getRequestsSubject, this.actRequestsSubject]).pipe(
      map(([getReqs, actReqs]) =>
        Object.values(getReqs).some((r) => r.contractKey === contractKey) ||
        Object.values(actReqs).some((r) => r.contractKey === contractKey),
      ),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getSubjectForMethod(method: string): BehaviorSubject<{ [key: string]: Request }> | null {
    if (method === 'GET') {
      return this.getRequestsSubject;
    }
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      return this.actRequestsSubject;
    }
    return null;
  }
}
